/**
 * TradeIntelligence — Global Trade Intelligence (Sea Imports)
 * -----------------------------------------------------------
 * Workflow:
 *   1. Admin types a product (e.g., "surgical gloves").
 *   2. (Optional) Click "Lookup HS code" — Claude returns the 6-digit HS code.
 *   3. We display ImportYeti / Panjiva search URLs for them to open.
 *   4. They export the CSV from those sites and paste it here.
 *   5. We parse, dedupe by consignee, rank by shipment count.
 *   6. One-click "Add as Supplier" creates a pending supplier with source=trade_intel.
 *
 * Why CSV paste? ImportYeti is behind Cloudflare + NextJS SPA — direct
 * server-scraping is blocked. The CSV export is the legal, reliable path.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Globe,
  Search,
  Sparkles,
  Loader2,
  Ship,
  Building2,
  ExternalLink,
  UserPlus,
  CheckCircle2,
  AlertCircle,
  Clipboard,
  Anchor,
  Package,
} from "lucide-react";
import { API, useAuth } from "@/App";

const buildAuthHeaders = (token) => ({ Authorization: `Bearer ${token}` });

function buildSearchLinks(query, hsCode) {
  const q = encodeURIComponent(query);
  const hs = hsCode ? encodeURIComponent(hsCode) : "";
  return [
    {
      name: "ImportYeti",
      url: `https://www.importyeti.com/search?q=${q}`,
      note: "Free tier — search, then click 'Export' (top-right of results table).",
    },
    {
      name: "Panjiva (S&P Global)",
      url: `https://panjiva.com/search?q=${q}`,
      note: "Trial accounts can export 50 rows. Best for HS-code specific queries.",
    },
    hs && {
      name: `US Census — HS ${hs}`,
      url: `https://usatrade.census.gov/`,
      note: "Aggregate import totals by HS code & country (no consignee names).",
    },
  ].filter(Boolean);
}

export default function TradeIntelligence() {
  const { token } = useAuth();
  const headers = useMemo(() => buildAuthHeaders(token), [token]);
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [hsCode, setHsCode] = useState("");
  const [hsConfidence, setHsConfidence] = useState(null);
  const [hsLoading, setHsLoading] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteLoading, setPasteLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const [convertingName, setConvertingName] = useState(null);
  const [converted, setConverted] = useState({}); // {name: supplier_id}

  useEffect(() => { loadRecent(); }, []);

  // Pre-fill from ?q= deep-link (e.g. opened from a Backorder)
  useEffect(() => {
    const q = searchParams.get("q");
    if (q && q !== query) {
      setQuery(q);
      // Auto-trigger cache lookup after state lands
      setTimeout(() => runSearch(q), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadRecent = async () => {
    try {
      const r = await axios.get(`${API}/trade-intel/recent-searches`, { headers });
      setRecent(r.data?.recent || []);
    } catch (_e) {
      // Silent — not critical
    }
  };

  const lookupHsCode = async () => {
    const desc = query.trim();
    if (!desc) {
      toast.error("Type a product description first");
      return;
    }
    setHsLoading(true);
    setHsConfidence(null);
    try {
      const r = await axios.post(
        `${API}/trade-intel/lookup-hs-code`,
        { product_description: desc },
        { headers }
      );
      setHsCode(r.data?.hs_code || "");
      setHsConfidence(r.data);
      toast.success(`HS code: ${r.data?.hs_code} (${r.data?.confidence || "?"} confidence)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "HS lookup failed");
    } finally {
      setHsLoading(false);
    }
  };

  const runSearch = async (qOverride) => {
    const q = (qOverride ?? query).trim();
    if (q.length < 3) {
      toast.error("Search query must be at least 3 characters");
      return;
    }
    if (qOverride) setQuery(qOverride);
    setSearchLoading(true);
    setConverted({});
    try {
      const r = await axios.post(
        `${API}/trade-intel/search`,
        { query: q, hs_code: hsCode || null },
        { headers }
      );
      setSnapshot(r.data);
      if (r.data?.results?.length) {
        toast.success(`Loaded ${r.data.result_count} cached consignees`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally {
      setSearchLoading(false);
    }
  };

  const submitPaste = async () => {
    const q = query.trim();
    if (q.length < 3) {
      toast.error("Type the product name above first");
      return;
    }
    if (pasteText.trim().length < 20) {
      toast.error("Paste at least one CSV row (with header)");
      return;
    }
    setPasteLoading(true);
    try {
      const r = await axios.post(
        `${API}/trade-intel/parse-paste`,
        { query: q, hs_code: hsCode || null, raw_text: pasteText },
        { headers }
      );
      setSnapshot(r.data);
      setPasteText("");
      setConverted({});
      toast.success(`Parsed ${r.data.raw_row_count} rows into ${r.data.result_count} consignees`);
      loadRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Parse failed");
    } finally {
      setPasteLoading(false);
    }
  };

  const convertToSupplier = async (row) => {
    setConvertingName(row.consignee_name);
    try {
      const r = await axios.post(
        `${API}/trade-intel/consignee-to-supplier`,
        {
          consignee_name: row.consignee_name,
          consignee_address: row.consignee_address,
          consignee_city: row.consignee_city,
          consignee_state: row.consignee_state,
          consignee_country: row.consignee_country,
          last_shipment_date: row.last_arrival,
          last_port: (row.ports || [])[0],
          product_keyword: query,
          hs_code: hsCode || null,
        },
        { headers }
      );
      setConverted((m) => ({ ...m, [row.consignee_name]: r.data.supplier_id }));
      toast.success(
        r.data.status === "exists"
          ? `Logged sighting on existing supplier "${r.data.name}"`
          : `Added "${r.data.name}" as pending supplier`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create supplier");
    } finally {
      setConvertingName(null);
    }
  };

  const searchLinks = useMemo(() => buildSearchLinks(query, hsCode), [query, hsCode]);
  const hasResults = !!(snapshot?.results?.length);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="trade-intel-page">
      {/* Header */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Globe className="w-6 h-6 text-amber-600" />
                Global Trade Intelligence
              </CardTitle>
              <CardDescription className="mt-1.5 text-sm max-w-3xl">
                Find the US hospitals, GPOs and distributors that already import a
                product by sea — then turn their overseas shippers into your next
                suppliers. Data sourced from US Customs Bills of Lading (public
                record) via ImportYeti / Panjiva CSV exports.
              </CardDescription>
            </div>
            <Ship className="w-12 h-12 text-amber-100 hidden sm:block" />
          </div>
        </CardHeader>
      </Card>

      {/* Step 1 — Query */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-amber-600 text-white text-xs font-bold flex items-center justify-center">1</span>
            Define the product
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid sm:grid-cols-[1fr_180px_auto] gap-3 items-end">
            <div>
              <Label className="text-xs">Product (e.g. "surgical gloves nitrile")</Label>
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="surgical gloves"
                data-testid="ti-query-input"
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
              />
            </div>
            <div>
              <Label className="text-xs">HS code (optional)</Label>
              <Input
                value={hsCode}
                onChange={(e) => setHsCode(e.target.value)}
                placeholder="401519"
                data-testid="ti-hs-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={lookupHsCode}
              disabled={hsLoading || !query.trim()}
              data-testid="ti-lookup-hs"
              className="gap-1.5"
            >
              {hsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              AI HS lookup
            </Button>
          </div>
          {hsConfidence && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2.5 space-y-1">
              <div><strong>HS {hsConfidence.hs_code}</strong> — {hsConfidence.description}</div>
              <div className="text-slate-600">Confidence: {hsConfidence.confidence}</div>
              {hsConfidence.alternates?.length > 0 && (
                <div className="text-slate-500">
                  Alternates: {hsConfidence.alternates.map(a => `${a.code} (${a.description})`).join(" · ")}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2 — Open in external sources */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-amber-600 text-white text-xs font-bold flex items-center justify-center">2</span>
            Run the search on a trade-data site
          </CardTitle>
          <CardDescription className="text-xs">
            Open one of these in your browser, run the search, then export the CSV.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!query.trim() ? (
            <p className="text-xs text-slate-500 italic">Type a product above to enable links.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {searchLinks.map((l) => (
                <a
                  key={l.name}
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-start gap-2 border border-slate-200 rounded-md p-3 hover:border-amber-400 hover:bg-amber-50/40 transition"
                  data-testid={`ti-link-${l.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  <ExternalLink className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs">
                    <div className="font-semibold text-slate-800">{l.name}</div>
                    <div className="text-slate-500 mt-0.5">{l.note}</div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 3 — Paste CSV */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-amber-600 text-white text-xs font-bold flex items-center justify-center">3</span>
            Paste the CSV export
          </CardTitle>
          <CardDescription className="text-xs">
            Paste the exported CSV/TSV below. We need at least a "Consignee" /
            "Buyer" / "Importer" column — everything else is optional.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={6}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={'Consignee,Address,City,State,Shipper,Country of Origin,Arrival Date,US Port,Product Description\nMedline Industries,3 Lakes Dr,Northfield,IL,Top Glove,Malaysia,2026-02-10,Long Beach CA,Nitrile gloves\n...'}
            className="font-mono text-xs"
            data-testid="ti-paste-textarea"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={submitPaste}
              disabled={pasteLoading || !query.trim() || pasteText.trim().length < 20}
              className="bg-amber-600 hover:bg-amber-700 gap-1.5"
              data-testid="ti-parse-btn"
            >
              {pasteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clipboard className="w-4 h-4" />}
              Parse & dedupe
            </Button>
            <Button
              variant="outline"
              onClick={() => runSearch()}
              disabled={searchLoading || !query.trim()}
              className="gap-1.5"
              data-testid="ti-search-cached-btn"
            >
              {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Load cached results
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 4 — Results */}
      {snapshot && (
        <Card data-testid="ti-results-card">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-amber-600 text-white text-xs font-bold flex items-center justify-center">4</span>
                  Consignees for "{snapshot.query}"
                </CardTitle>
                <CardDescription className="text-xs mt-1">
                  {hasResults
                    ? <>Ranked by shipment count.{snapshot.from_cache ? <> · Cached {snapshot.cache_age_hours}h ago.</> : null}</>
                    : "No data yet — paste a CSV above to populate."}
                </CardDescription>
              </div>
              {hasResults && (
                <div className="flex gap-2 flex-wrap">
                  <Badge variant="outline" className="gap-1">
                    <Building2 className="w-3 h-3" /> {snapshot.result_count} consignees
                  </Badge>
                  {snapshot.raw_row_count != null && (
                    <Badge variant="outline" className="gap-1">
                      <Package className="w-3 h-3" /> {snapshot.raw_row_count} shipments
                    </Badge>
                  )}
                  {snapshot.hs_code && (
                    <Badge variant="outline">HS {snapshot.hs_code}</Badge>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!hasResults ? (
              <div className="text-center py-10 text-sm text-slate-500">
                <AlertCircle className="w-8 h-8 mx-auto text-amber-400 mb-2" />
                {snapshot.message || snapshot.error || "No consignees in this snapshot."}
              </div>
            ) : (
              <div className="space-y-2">
                {snapshot.results.map((r, idx) => {
                  const convertedId = converted[r.consignee_name];
                  return (
                    <ConsigneeRow
                      key={`${r.consignee_name}-${idx}`}
                      row={r}
                      idx={idx}
                      converting={convertingName === r.consignee_name}
                      convertedId={convertedId}
                      onConvert={() => convertToSupplier(r)}
                    />
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent searches */}
      {recent.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Anchor className="w-4 h-4 text-slate-500" />
              Recent searches
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {recent.slice(0, 20).map((r) => (
                <button
                  key={r.query}
                  onClick={() => { setQuery(r.query); setHsCode(r.hs_code || ""); runSearch(r.query); }}
                  className="text-xs px-2.5 py-1.5 border border-slate-200 rounded-full hover:border-amber-400 hover:bg-amber-50 transition"
                  data-testid={`ti-recent-${r.query.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                >
                  {r.query}
                  {r.result_count > 0 && <span className="text-slate-400 ml-1.5">· {r.result_count}</span>}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ConsigneeRow({ row, idx, converting, convertedId, onConvert }) {
  return (
    <div
      className="border border-slate-200 rounded-md p-3 hover:border-amber-300 transition bg-white"
      data-testid={`ti-consignee-row-${idx}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 text-sm">{row.consignee_name}</span>
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
              {row.shipment_count} shipment{row.shipment_count === 1 ? "" : "s"}
            </Badge>
            {row.consignee_state && (
              <span className="text-xs text-slate-500">
                {row.consignee_city ? `${row.consignee_city}, ` : ""}{row.consignee_state}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-600 mt-1.5 space-y-0.5">
            {row.shippers?.length > 0 && (
              <div><strong className="text-slate-500 font-medium">Shippers:</strong> {row.shippers.join(", ")}</div>
            )}
            {row.origins?.length > 0 && (
              <div><strong className="text-slate-500 font-medium">Origins:</strong> {row.origins.join(", ")}</div>
            )}
            {row.ports?.length > 0 && (
              <div><strong className="text-slate-500 font-medium">Ports:</strong> {row.ports.join(", ")}</div>
            )}
            {row.last_arrival && (
              <div><strong className="text-slate-500 font-medium">Last arrival:</strong> {row.last_arrival}{row.first_arrival && row.first_arrival !== row.last_arrival ? ` (since ${row.first_arrival})` : ""}</div>
            )}
          </div>
        </div>
        <div className="shrink-0">
          {convertedId ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1">
              <CheckCircle2 className="w-3 h-3" /> Added
            </Badge>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={onConvert}
              disabled={converting}
              className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid={`ti-convert-btn-${idx}`}
            >
              {converting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
              Add as Supplier
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
